import React from "react";
// import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
// import { Tooltip as ShadTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
// import { Badge } from "@/components/ui/badge";
import { Info, Leaf, Users, ShieldCheck, CircleDot } from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";

import "../assets/styles/NeilHarleyESG.css";

// ------------------------
// Card wrapper components
// ------------------------
const Card = ({ children }) => <div className="card">{children}</div>;
const CardHeader = ({ children }) => <div className="card-header">{children}</div>;
const CardContent = ({ children }) => <div>{children}</div>;

// -------------------------
// Mock data
// -------------------------
const mock = {
  farm_id: "GWS_35_127_0043",
  farm_name: "X10AI_FT_050",
  reporting_period: "Reporting Period: 2024–2025",
  esg_composite_score: 84,
  weights: { environmental: 0.7, social: 0.15, governance: 0.15 },
  environmental: {
    score: 89,
    soil_health: { score: 84, pH: 6.3, P_index: 2, SOM_pct: 5.1, C_pct: 2.7 },
    nutrient_circularity: { N_retention_pct: 83, P_retention_pct: 91, K_retention_pct: 92, score: 88 },
    SOC_trend_tC_per_ha: 0.18,
    runoff_risk: "low",
    biodiversity: { GS4_area_pct: 36, SW7_SW14_area_pct: 7, hedgerow_area_pct: 6, score: 82 },
    energy_carbon_circularity: { score: 94 },
  },
  social: { score: 86, animal_welfare: 92, innovation: 95, local_employment: 70 },
  governance: { score: 77, compliance: 80, traceability: 74, audit: 75 },
  priority_actions: [
    "Extend GS4/SW14 buffers along Poppies & Shooters Hill",
    "Integrate 2025 drone biodiversity verification",
    "Deploy N-tracking sensors via FarmTwin BlackBox",
  ],
};

// -------------------------
// Helpers & thresholds
// -------------------------
const COLORS = {
  env: "#2E7D32",
  envLight: "#A5D6A7",
  soc: "#F9A825",
  gov: "#1565C0",
  govLight: "#90CAF9",
  red: "#EF4444",
  amber: "#F59E0B",
  green: "#22C55E",
  blue: "#3B82F6",
  gray: "#6B7280",
};

function bandForScore(score) {
  if (score >= 90) return "blue";
  if (score >= 75) return "green";
  if (score >= 60) return "amber";
  return "red";
}

function bandColor(b) {
  switch (b) {
    case "red":
      return COLORS.red;
    case "amber":
      return COLORS.amber;
    case "green":
      return COLORS.green;
    case "blue":
      return COLORS.blue;
    default:
      return COLORS.gray;
  }
}

// ring gauge via SVG
function Gauge({ value, size = 180, stroke = 16 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const offset = c - (pct / 100) * c;
  const band = bandForScore(value);
  return (
    <svg width={size} height={size} className="overflow-visible">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="#E5E7EB" strokeWidth={stroke} fill="none" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke={bandColor(band)}
        strokeWidth={stroke}
        fill="none"
        strokeDasharray={`${c} ${c}`}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" className="fill-gray-700">
        <tspan className="text-3xl font-bold">{value.toFixed(1)}</tspan>
        <tspan className="text-sm">/100</tspan>
      </text>
    </svg>
  );
}

const BandBadge = ({ score }) => {
  const b = bandForScore(score);
  const label = b === "blue" ? "Exemplary" : b === "green" ? "Good" : b === "amber" ? "Progressing" : "Needs Action";
  return <span className="badge" style={{ background: bandColor(b) }}>{label}</span>;
};

const Small = ({ children }) => <div className="text-xs text-gray-500 mt-1">{children}</div>;

// -------------------------
// Charts data transforms
// -------------------------
const soilRadar = [
  { metric: "pH", value: (mock.environmental.soil_health.pH / 7.0) * 100 },
  { metric: "P-Index", value: (mock.environmental.soil_health.P_index / 3) * 100 },
  { metric: "SOM %", value: (mock.environmental.soil_health.SOM_pct / 6) * 100 },
  { metric: "C %", value: (mock.environmental.soil_health.C_pct / 3.5) * 100 },
];

const biodiversityStack = [
  {
    name: "Habitat",
    GS4: mock.environmental.biodiversity.GS4_area_pct,
    Buffers: mock.environmental.biodiversity.SW7_SW14_area_pct,
    Hedges: mock.environmental.biodiversity.hedgerow_area_pct,
    Arable:
      100 -
      (mock.environmental.biodiversity.GS4_area_pct +
        mock.environmental.biodiversity.SW7_SW14_area_pct +
        mock.environmental.biodiversity.hedgerow_area_pct),
  },
];

const nutrientWheel = [
  { name: "N retained", value: mock.environmental.nutrient_circularity.N_retention_pct },
  { name: "P retained", value: mock.environmental.nutrient_circularity.P_retention_pct },
  { name: "K retained", value: mock.environmental.nutrient_circularity.K_retention_pct },
  {
    name: "Loss allowance",
    value:
      100 -
      (mock.environmental.nutrient_circularity.N_retention_pct +
        mock.environmental.nutrient_circularity.P_retention_pct +
        mock.environmental.nutrient_circularity.K_retention_pct) /
        3,
  },
];

const carbonSeries = [
  { year: 2023, tC_ha: 0.1 },
  { year: 2024, tC_ha: 0.16 },
  { year: 2025, tC_ha: mock.environmental.SOC_trend_tC_per_ha },
  { year: 2026, tC_ha: 0.19 },
  { year: 2027, tC_ha: 0.2 },
  { year: 2028, tC_ha: 0.2 },
  { year: 2029, tC_ha: 0.21 },
  { year: 2030, tC_ha: 0.21 },
];

const contribution = [
  { name: "Environmental", pts: +(mock.weights.environmental * mock.environmental.score).toFixed(1) },
  { name: "Social", pts: +(mock.weights.social * mock.social.score).toFixed(1) },
  { name: "Governance", pts: +(mock.weights.governance * mock.governance.score).toFixed(1) },
];

// -------------------------
// UI Sections
// -------------------------

// ------------------------
// Panels
// ------------------------
function EnvironmentalPanel() {
  return (
    <div
        className="grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '10px',
        }}
    >
      {/* Soil Health */}
      <Card style={{ gridColumn: 'span 4' }}>
        <CardHeader>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.25rem', margin: 0 }}>
            <Leaf style={{ width: 20, height: 20 }} /> Soil Health
          </h3>
        </CardHeader>
        <CardContent>
          <div style={{ height: 224 }}>
            <ResponsiveContainer>
              <RadarChart data={soilRadar} outerRadius={80}>
                <PolarGrid />
                <PolarAngleAxis dataKey="metric" />
                <PolarRadiusAxis tick={false} angle={30} domain={[0, 100]} />
                <Radar dataKey="value" stroke={COLORS.env} fill={COLORS.envLight} fillOpacity={0.6} />
                <Tooltip formatter={(v) => `${v.toFixed(0)}% of target`} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginTop: '8px', fontSize: '0.875rem' }}>
            <div>pH <span style={{ fontWeight: 'bold' }}>{mock.environmental.soil_health.pH}</span></div>
            <div>P-Idx <span style={{ fontWeight: 'bold' }}>{mock.environmental.soil_health.P_index}</span></div>
            <div>SOM <span style={{ fontWeight: 'bold' }}>{mock.environmental.soil_health.SOM_pct}%</span></div>
            <div>C <span style={{ fontWeight: 'bold' }}>{mock.environmental.soil_health.C_pct}%</span></div>
          </div>
        </CardContent>
      </Card>

      {/* Nutrient Circularity */}
      <Card style={{ gridColumn: 'span 4' }}>
        <CardHeader>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.25rem', margin: 0 }}>
            <CircleDot style={{ width: 20, height: 20 }} /> Nutrient Circularity
          </h3>
        </CardHeader>
        <CardContent>
          <div style={{ height: 224 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={nutrientWheel} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80}>
                  {nutrientWheel.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={[COLORS.env, "#7FB77E", "#4CAF50", COLORS.gray][index]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '8px', fontSize: '0.875rem' }}>
            <div>N: <span style={{ fontWeight: 'bold' }}>{mock.environmental.nutrient_circularity.N_retention_pct}%</span></div>
            <div>P: <span style={{ fontWeight: 'bold' }}>{mock.environmental.nutrient_circularity.P_retention_pct}%</span></div>
            <div>K: <span style={{ fontWeight: 'bold' }}>{mock.environmental.nutrient_circularity.K_retention_pct}%</span></div>
          </div>
        </CardContent>
      </Card>

      {/* Nutrient Loss Index */}
      <Card style={{ gridColumn: 'span 4' }}>
        <CardHeader>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.25rem', margin: 0 }}>
            Nutrient Loss Index <Info style={{ width: 16, height: 16, color: '#6B7280' }} />
          </h3>
        </CardHeader>
        <CardContent>
          <div style={{ height: 224, display: 'flex', alignItems: 'center' }}>
            <ResponsiveContainer>
              <BarChart data={[{ name: "Loss", value: 6.2, target: 8 }]}>
                <XAxis dataKey="name" hide />
                <YAxis domain={[0, 12]} />
                <Tooltip />
                <Bar dataKey="value" fill={COLORS.green} />
                <LineChart data={[{ x: 0, y: 8 }, { x: 1, y: 8 }]}>
                  <Line type="monotone" dataKey="y" stroke="#EF4444" dot={false} />
                </LineChart>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <Small>Target ≤ 8 kg N eq./ha</Small>
        </CardContent>
      </Card>

      {/* Soil Carbon Trend */}
      <Card style={{ gridColumn: 'span 6' }}>
        <CardHeader>
          <h3 style={{ fontSize: '1.25rem', margin: 0 }}>Soil Carbon Trend</h3>
        </CardHeader>
        <CardContent>
          <div style={{ height: 224 }}>
            <ResponsiveContainer>
              <LineChart data={carbonSeries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <XAxis dataKey="year" />
                <YAxis domain={[0, 0.3]} tickFormatter={(v) => `${v.toFixed(2)}`} />
                <Tooltip formatter={(v) => `${v.toFixed(2)} t C/ha`} />
                <Line type="monotone" dataKey="tC_ha" stroke={COLORS.env} strokeWidth={2} dot />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <Small>Δ SOC = {mock.environmental.SOC_trend_tC_per_ha} t C/ha/yr (target ≥ 0.15)</Small>
        </CardContent>
      </Card>

      {/* Biodiversity Composition */}
      <Card style={{ gridColumn: 'span 6' }}>
        <CardHeader>
          <h3 style={{ fontSize: '1.25rem', margin: 0 }}>Biodiversity & Habitat Composition</h3>
        </CardHeader>
        <CardContent>
          <div style={{ height: 224 }}>
            <ResponsiveContainer>
              <BarChart data={biodiversityStack}>
                <XAxis dataKey="name" hide />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Bar dataKey="GS4" stackId="a" fill="#22C55E" />
                <Bar dataKey="Buffers" stackId="a" fill="#86EFAC" />
                <Bar dataKey="Hedges" stackId="a" fill="#15803D" />
                <Bar dataKey="Arable" stackId="a" fill="#D1D5DB" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <Small>Threshold ≥ 40 % combined habitat equivalent</Small>
        </CardContent>
      </Card>
    </div>
  );
}

function SocialPanel() {
  return (
    <div className="grid-panel">
      <Card>
        <CardHeader>Animal Welfare</CardHeader>
        <CardContent>
          <Gauge value={mock.social.animal_welfare} size={120} />
          <Small>Outwintered herd · rotational leys</Small>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>Innovation</CardHeader>
        <CardContent>
          <Gauge value={mock.social.innovation} size={120} />   
          <Small>Pilot Farm ✓ · data sharing</Small>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>Local Employment</CardHeader>
        <CardContent>
          <Gauge value={mock.social.local_employment} size={120} />
          <Small>Local labour ratio</Small>
        </CardContent>
      </Card>
    </div>
  );
}

function GovernancePanel() {
  return (
    <div className="grid-panel">
      <Card>
        <CardHeader>Compliance</CardHeader>
        <CardContent>
          <Gauge value={mock.governance.compliance} size={120} />
          <Small>Organic conversion · records</Small>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>Traceability</CardHeader>
        <CardContent>
          <Gauge value={mock.governance.traceability} size={120} />
          <Small>FarmTwin → ABP chain</Small>
          </CardContent>
      </Card>
      <Card>
        <CardHeader>Audit</CardHeader>
        <CardContent>
          <Gauge value={mock.governance.audit} size={120} />        
          <Small>Environmental & input logs</Small>
        </CardContent>
      </Card>
    </div>
  );
}

// Footer Summary
function FooterSummary() {
  return (
    <Card>
      <CardHeader>Composite ESG Summary</CardHeader>
      <CardContent>
        <div className="grid-panel">
          <Gauge value={mock.esg_composite_score} />
          <div>
            {contribution.map(c=>(
              <div key={c.name} style={{marginBottom:8}}>
                <div>{c.name}</div>
                <Small>{c.pts} pts</Small>
              </div>
            ))}
            <div>
              <div>Priority Actions:</div>
              <ul>
                {mock.priority_actions.map(a=><li key={a}>{a}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ------------------------
// Main Dashboard
// ------------------------
export default function FarmTwinESGDashboard() {
  return (
    <div className="dashboard">
      {/* Top Bar */}
      <div className="top-bar">
        {/* Farm Info */}
        <div className="farm-info">
          <h1>{mock.farm_name}</h1>
          <div className="reporting-period">{mock.reporting_period}</div>
        </div>

        {/* ESG Composite */}
        <Card>
          <CardContent>
            <Gauge value={mock.esg_composite_score} size={140} />
            <div>Overall Score</div>
          </CardContent>
        </Card>

        {/* Mini ESG Cards with Gauges */}
        <div className="mini-scores">
          <Card>
            <CardContent style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <Gauge value={mock.environmental.score} size={110} />
              <div><Leaf />Environmental</div>
              <Small><BandBadge score={mock.environmental.score} /></Small>
            </CardContent>
          </Card>

          <Card>
            <CardContent style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <Gauge value={mock.social.score} size={110} />
              <div><Users />Social</div>
              <Small><BandBadge score={mock.social.score} /></Small>
            </CardContent>
          </Card>

          <Card>
            <CardContent style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <Gauge value={mock.governance.score} size={110} />
              <div><ShieldCheck />Governance</div>
              <Small><BandBadge score={mock.governance.score} /></Small>
            </CardContent>
          </Card>
        </div>
      </div>


      {/* Sections */}
      <section className="section">
        <h2><Leaf /> Environmental Performance</h2>
        <EnvironmentalPanel />
      </section>

      <section className="section">
        <h2><Users /> Social Performance</h2>
        <SocialPanel />
      </section>

      <section className="section">
        <h2><ShieldCheck /> Governance</h2>
        <GovernancePanel />
      </section>

      <FooterSummary />
    </div>
  );
}